import { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import type { Product } from '@/mocks/products';
import { useTheme } from '@/context/ThemeContext';
import { useCompare } from '@/context/CompareContext';
import { useProductCatalog } from '@/context/ProductCatalogContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

/* ─── helpers ─── */
const categoryColor: Record<string, string> = {
  lithium: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
  agm: 'text-sky-400 bg-sky-400/10 border-sky-400/30',
  gel: 'text-violet-400 bg-violet-400/10 border-violet-400/30',
};

const categoryColorLight: Record<string, string> = {
  lithium: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  agm: 'text-sky-700 bg-sky-50 border-sky-200',
  gel: 'text-violet-700 bg-violet-50 border-violet-200',
};

function Bar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="w-full h-2 rounded-full bg-current opacity-10 overflow-hidden">
      <div
        className={`h-full rounded-full ${color}`}
        style={{ width: `${pct}%`, opacity: 1 }}
      />
    </div>
  );
}

interface RowProps {
  label: string;
  icon: string;
  values: (string | number | null)[];
  unit?: string;
  highlight?: boolean;
  light: boolean;
  maxVal?: number;
  barColor?: string;
}

function CompareColGroup({ colCount }: { colCount: number }) {
  return (
    <colgroup>
      <col style={{ width: '13rem' }} />
      {Array.from({ length: colCount }).map((_, index) => (
        <col key={index} />
      ))}
      {colCount < 3 && <col />}
    </colgroup>
  );
}

function CompareRow({ label, icon, values, unit = '', highlight = false, light, maxVal, barColor }: RowProps) {
  const rowBg = highlight
    ? 'bg-[var(--accent)]/5'
    : '';
  const labelColor = light ? 'text-gray-500' : 'text-white/40';
  const valColor = light ? 'text-gray-900' : 'text-white';
  const borderColor = light ? 'border-gray-100' : 'border-white/5';

  const numericVals = values.filter((v) => v !== null && v !== '—') as number[];
  const bestVal = numericVals.length > 0 ? Math.max(...numericVals) : null;

  return (
    <tr className={`border-b ${borderColor} ${rowBg}`}>
      <td className={`w-52 py-3 px-4 align-middle text-xs font-medium leading-relaxed ${labelColor}`}>
        <span className="flex items-center gap-2">
          <i className={icon} style={{ color: 'var(--accent)' }}></i>
          {label}
        </span>
      </td>
      {values.map((val, i) => {
        const isNull = val === null || val === '—';
        const isBest = !isNull && typeof val === 'number' && val === bestVal && numericVals.length > 1;
        return (
          <td key={i} className="py-3 px-4 align-middle text-center">
            <div className="flex flex-col items-center gap-1">
              <span
                className={`text-sm font-semibold ${
                  isNull
                    ? light ? 'text-gray-300' : 'text-white/20'
                    : valColor
                }`}
                style={isBest ? { color: 'var(--accent)' } : {}}
              >
                {isNull ? '—' : `${val}${unit}`}
                {isBest && (
                  <span
                    className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', color: 'var(--accent)' }}
                  >
                    BEST
                  </span>
                )}
              </span>
              {maxVal && !isNull && typeof val === 'number' && (
                <div className="w-20">
                  <Bar value={val} max={maxVal} color={barColor || 'bg-sky-400'} />
                </div>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

/* ─── main ─── */
export default function ComparePage() {
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { clear } = useCompare();
  const { products } = useProductCatalog();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';

  const ids = useMemo(() => {
    const raw = searchParams.get('ids') || '';
    return raw.split(',').filter(Boolean).slice(0, 3);
  }, [searchParams]);

  const compared: Product[] = useMemo(
    () => ids.map((id) => products.find((p) => p.id === id)).filter(Boolean) as Product[],
    [ids, products]
  );

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const cardBg = light ? 'bg-white border border-gray-100' : 'bg-[#1C2B3A] border border-white/5';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const bodyColor = light ? 'text-gray-500' : 'text-white/50';
  const tableBg = light ? 'bg-white border border-gray-100' : 'bg-[#1C2B3A] border border-white/5';
  const sectionLabel = light ? 'bg-gray-50 text-gray-400' : 'bg-[#0D1B2A]/60 text-white/30';
  const borderColor = light ? 'border-gray-100' : 'border-white/5';

  if (compared.length < 2) {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-6">
          <div
            className="w-16 h-16 flex items-center justify-center rounded-full"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}
          >
            <i className="ri-bar-chart-grouped-line text-3xl" style={{ color: 'var(--accent)' }}></i>
          </div>
          <h2 className={`text-2xl font-bold ${headingColor}`}>No products selected</h2>
          <p className={`text-sm ${bodyColor} text-center max-w-sm`}>
            Select at least 2 products from the catalog to compare them side by side.
          </p>
          <Link
            to="/products"
            className="flex items-center gap-2 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            <i className="ri-arrow-left-line"></i>
            Back to Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const colCount = compared.length;

  const specRows: RowProps[] = [
    {
      label: 'Price',
      icon: 'ri-price-tag-3-line',
      values: compared.map((p) => p.price),
      unit: '',
      highlight: true,
      light,
      maxVal: Math.max(...compared.map((p) => p.price)),
      barColor: 'bg-sky-400',
    },
    {
      label: 'Capacity',
      icon: 'ri-flashlight-line',
      values: compared.map((p) => p.capacity),
      unit: 'Ah',
      light,
      maxVal: Math.max(...compared.map((p) => p.capacity)),
      barColor: 'bg-emerald-400',
    },
    {
      label: 'Voltage',
      icon: 'ri-plug-line',
      values: compared.map((p) => p.voltage),
      unit: 'V',
      light,
    },
    {
      label: 'Weight',
      icon: 'ri-scales-3-line',
      values: compared.map((p) => p.weight),
      unit: ' lbs',
      light,
      maxVal: Math.max(...compared.map((p) => p.weight)),
      barColor: 'bg-sky-400',
    },
    {
      label: 'CCA',
      icon: 'ri-speed-line',
      values: compared.map((p) => (p.cca > 0 ? p.cca : null)),
      unit: 'A',
      light,
    },
    {
      label: 'Dimensions',
      icon: 'ri-ruler-line',
      values: compared.map((p) => p.dimensions),
      light,
    },
  ];

  const perfRows: RowProps[] = [
    {
      label: 'Cycle Life',
      icon: 'ri-refresh-line',
      values: compared.map((p) => p.performance.cycleLife),
      unit: ' cycles',
      highlight: true,
      light,
      maxVal: Math.max(...compared.map((p) => p.performance.cycleLife)),
      barColor: 'bg-emerald-400',
    },
    {
      label: 'Efficiency',
      icon: 'ri-percent-line',
      values: compared.map((p) => p.performance.efficiency),
      unit: '%',
      light,
      maxVal: 100,
      barColor: 'bg-sky-400',
    },
    {
      label: 'Energy Density',
      icon: 'ri-battery-charge-line',
      values: compared.map((p) => p.performance.energyDensity),
      unit: ' Wh/kg',
      light,
      maxVal: Math.max(...compared.map((p) => p.performance.energyDensity)),
      barColor: 'bg-violet-400',
    },
    {
      label: 'Charge Time',
      icon: 'ri-time-line',
      values: compared.map((p) => p.performance.chargeTime),
      unit: 'h',
      light,
    },
    {
      label: 'Self-Discharge',
      icon: 'ri-arrow-down-circle-line',
      values: compared.map((p) => p.performance.selfDischargeMonthly),
      unit: '%/mo',
      light,
    },
    {
      label: 'Weight (kg)',
      icon: 'ri-scales-3-line',
      values: compared.map((p) => p.performance.weightKg),
      unit: ' kg',
      light,
      maxVal: Math.max(...compared.map((p) => p.performance.weightKg)),
      barColor: 'bg-sky-400',
    },
  ];

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title="Compare Marine Batteries | Stingray"
        description="Compare Stingray marine batteries side by side — specs, performance, price and features."
        keywords="compare marine batteries, battery comparison, lithium vs AGM, marine battery specs"
        canonical="https://stingray-marine.com/products/compare"
      />
      <Navbar />

      {/* Header */}
      <header className="pt-32 pb-10 px-6 md:px-10">
        <nav className={`flex items-center gap-2 text-xs mb-4 ${light ? 'text-gray-400' : 'text-white/30'}`}>
          <a href="/" className="hover:text-[var(--accent)] transition-colors">Home</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/products" className="hover:text-[var(--accent)] transition-colors">Products</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className={light ? 'text-gray-600' : 'text-white/60'}>Compare</span>
        </nav>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            {settings.productBadges && (
              <span
                className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-3"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}
              >
                Side-by-Side Comparison
              </span>
            )}
            <h1 className={`text-3xl md:text-4xl font-black ${headingColor}`}>
              Compare Batteries
            </h1>
            <p className={`text-sm mt-2 ${bodyColor}`}>
              Comparing {colCount} products — select up to 3 from the catalog.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/products"
              onClick={clear}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                light ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/10 text-white hover:bg-white/15'
              }`}
            >
              <i className="ri-add-line"></i>
              Change Products
            </Link>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-10 pb-24 space-y-8">

        {/* Product Header Cards */}
        <div className={`rounded-xl overflow-hidden ${tableBg}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] table-fixed">
              <CompareColGroup colCount={colCount} />
              <thead>
                <tr className={`border-b ${borderColor}`}>
                  <th className="w-52 py-4 px-4 text-left">
                    <span className={`text-xs font-semibold ${light ? 'text-gray-400' : 'text-white/30'}`}>
                      PRODUCT
                    </span>
                  </th>
                  {compared.map((p) => (
                    <th key={p.id} className="py-4 px-4 text-center align-top">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-full h-36 rounded-lg overflow-hidden bg-[#0D1B2A]">
                          <img
                            src={p.image}
                            alt={p.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div>
                          <span
                            className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize mb-1 ${
                              light ? categoryColorLight[p.category] : categoryColor[p.category]
                            }`}
                          >
                            {p.category}
                          </span>
                          <p className={`font-bold text-sm ${headingColor}`}>{p.name}</p>
                          <p className={`text-xs mt-0.5 ${bodyColor}`}>{p.tagline}</p>
                          <p className="font-black text-xl mt-2" style={{ color: 'var(--accent)' }}>${p.price.toLocaleString()}</p>
                        </div>
                        <Link
                          to={`/products/${p.id}`}
                          className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-white text-xs font-semibold transition-colors whitespace-nowrap"
                          style={{ backgroundColor: 'var(--accent)' }}
                        >
                          View Details <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </th>
                  ))}
                  {/* Empty slot if < 3 */}
                  {colCount < 3 && (
                    <th className="py-4 px-4 text-center align-top">
                      <Link
                        to="/products"
                        className={`flex flex-col items-center justify-center gap-2 h-36 rounded-lg border-2 border-dashed transition-colors ${
                          light
                            ? 'border-gray-200 text-gray-400 hover:text-[var(--accent)]'
                            : 'border-white/10 text-white/20 hover:text-[var(--accent)]'
                        }`}
                        style={{ ['--tw-border-opacity' as string]: '1' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = ''; }}
                      >
                        <i className="ri-add-circle-line text-2xl"></i>
                        <span className="text-xs font-medium">Add Product</span>
                      </Link>
                    </th>
                  )}
                </tr>
              </thead>
            </table>
          </div>
        </div>

        {/* Specs Table */}
        <div className={`rounded-xl overflow-hidden ${tableBg}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] table-fixed">
              <CompareColGroup colCount={colCount} />
              <thead>
                <tr>
                  <td
                    colSpan={colCount + 1 + (colCount < 3 ? 1 : 0)}
                    className={`py-2.5 px-4 text-xs font-bold tracking-widest uppercase ${sectionLabel}`}
                  >
                    <i className="ri-list-check-2 mr-2"></i>
                    Specifications
                  </td>
                </tr>
              </thead>
              <tbody>
                {specRows.map((row) => (
                  <CompareRow key={row.label} {...row} values={row.values} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Performance Table */}
        <div className={`rounded-xl overflow-hidden ${tableBg}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] table-fixed">
              <CompareColGroup colCount={colCount} />
              <thead>
                <tr>
                  <td
                    colSpan={colCount + 1 + (colCount < 3 ? 1 : 0)}
                    className={`py-2.5 px-4 text-xs font-bold tracking-widest uppercase ${sectionLabel}`}
                  >
                    <i className="ri-line-chart-line mr-2"></i>
                    Performance
                  </td>
                </tr>
              </thead>
              <tbody>
                {perfRows.map((row) => (
                  <CompareRow key={row.label} {...row} values={row.values} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Features Table */}
        <div className={`rounded-xl overflow-hidden ${tableBg}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] table-fixed">
              <CompareColGroup colCount={colCount} />
              <thead>
                <tr>
                  <td
                    colSpan={colCount + 1 + (colCount < 3 ? 1 : 0)}
                    className={`py-2.5 px-4 text-xs font-bold tracking-widest uppercase ${sectionLabel}`}
                  >
                    <i className="ri-star-line mr-2"></i>
                    Features
                  </td>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const allFeatures = Array.from(
                    new Set(compared.flatMap((p) => p.features))
                  );
                  return allFeatures.map((feat) => (
                    <tr key={feat} className={`border-b ${borderColor}`}>
                      <td className={`w-52 py-3 px-4 align-middle text-xs font-medium leading-relaxed ${light ? 'text-gray-500' : 'text-white/40'}`}>
                        <span className="flex items-center gap-2">
                          <i className="ri-checkbox-circle-line shrink-0" style={{ color: 'var(--accent)' }}></i>
                          <span>{feat}</span>
                        </span>
                      </td>
                      {compared.map((p) => {
                        const has = p.features.includes(feat);
                        return (
                          <td key={p.id} className="py-3 px-4 align-middle text-center">
                            {has ? (
                              <span className="inline-flex items-center justify-center w-6 h-6">
                                <i className="ri-check-line text-emerald-400 text-base font-bold"></i>
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-6 h-6">
                                <i className={`ri-close-line text-base ${light ? 'text-gray-300' : 'text-white/20'}`}></i>
                              </span>
                            )}
                          </td>
                        );
                      })}
                      {colCount < 3 && <td />}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Full Spec Sheet */}
        <div className={`rounded-xl overflow-hidden ${tableBg}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] table-fixed">
              <CompareColGroup colCount={colCount} />
              <thead>
                <tr>
                  <td
                    colSpan={colCount + 1 + (colCount < 3 ? 1 : 0)}
                    className={`py-2.5 px-4 text-xs font-bold tracking-widest uppercase ${sectionLabel}`}
                  >
                    <i className="ri-file-list-3-line mr-2"></i>
                    Full Spec Sheet
                  </td>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const allKeys = Array.from(
                    new Set(compared.flatMap((p) => Object.keys(p.specs)))
                  );
                  return allKeys.map((key) => (
                    <tr key={key} className={`border-b ${borderColor}`}>
                      <td className={`w-52 py-3 px-4 align-middle text-xs font-medium leading-relaxed ${light ? 'text-gray-500' : 'text-white/40'}`}>
                        {key}
                      </td>
                      {compared.map((p) => (
                        <td key={p.id} className={`py-3 px-4 align-middle text-center text-sm font-medium ${light ? 'text-gray-800' : 'text-white'}`}>
                          {p.specs[key] || <span className={light ? 'text-gray-300' : 'text-white/20'}>—</span>}
                        </td>
                      ))}
                      {colCount < 3 && <td />}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA */}
        <div className={`rounded-xl p-8 text-center ${cardBg}`}>
          <h3 className={`text-xl font-bold mb-2 ${headingColor}`}>Ready to choose?</h3>
          <p className={`text-sm mb-6 ${bodyColor}`}>
            Contact our team for expert advice on the best battery for your vessel.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/contact"
              className="flex items-center gap-2 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              <i className="ri-customer-service-2-line"></i>
              Get Expert Advice
            </Link>
            <Link
              to="/products"
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap ${
                light ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/10 text-white hover:bg-white/15'
              }`}
            >
              <i className="ri-arrow-left-line"></i>
              Back to Catalog
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
